import React, { useState, useEffect, useCallback } from 'react';
import { FaUsers, FaBook, FaDownload, FaLayerGroup, FaBookReader, FaFileAlt, FaSyncAlt } from 'react-icons/fa';
import LoadingScreen from '../components/LoadingScreen';
import LoadingOverlay from '../components/LoadingOverlay';
import DateRangeFilter from '../components/DateRangeFilter';
import ActiveUsersHeatmap from '../components/ActiveUsersHeatmap';
import ReadingLevelsChart from '../components/ReadingLevelsChart';
import PlatformChart from '../components/PlatformChart';
import ThemeToggle from '../components/ThemeToggle';
import {
  fetchEtsViewStatistics,
  fetchActiveUsersPerCountry,
  fetchReadingLevelStatistics,
  fetchPlatformStatistics
} from '../services/api';
import { formatCompactNumber } from '../utils/formatters';
import './Pages.css';
import './DataPages.css';

const EtsAnalytics = ({ isDarkMode, toggleDarkMode }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Summary stats
  const [stats, setStats] = useState({
    activeUsers: 0,
    viewBooks: 0,
    viewDownloads: 0,
    viewSeries: 0,
    booksRead: 0,
    pagesRead: 0
  });

  // Chart data
  const [countryData, setCountryData] = useState([]);
  const [readingLevelsData, setReadingLevelsData] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [earliestDate, setEarliestDate] = useState(null);

  // Date range helper (UTC to match server time)
  const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29);
    return {
      startDate: toUTCDateStr(start),
      endDate: toUTCDateStr(end)
    };
  };

  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);

  // Get days between dates
  const getDaysBetween = (start, end) => {
    const startD = new Date(start);
    const endD = new Date(end);
    const diffTime = Math.abs(endD - startD);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Load data
  const loadData = useCallback(async (isRefresh = false, customStartDate = null, customEndDate = null) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const fetchStartDate = customStartDate ?? startDate;
      const fetchEndDate = customEndDate ?? endDate;

      // Timed fetch helper
      const timedFetch = async (label, fn) => {
        const start = performance.now();
        const result = await fn();
        const duration = ((performance.now() - start) / 1000).toFixed(2);
        return { label, result, duration };
      };

      const results = await Promise.all([
        timedFetch('Statistics (Books Read, Pages Read, Active Users, View Books, View Downloads, View Series)', () => fetchEtsViewStatistics(fetchStartDate, fetchEndDate)),
        timedFetch('Active Users per Country (Heatmap)', () => fetchActiveUsersPerCountry(fetchStartDate, fetchEndDate)),
        timedFetch('Reading Levels (Chart)', () => fetchReadingLevelStatistics(fetchStartDate, fetchEndDate)),
        timedFetch('Platform Usage (Chart)', () => fetchPlatformStatistics(fetchStartDate, fetchEndDate))
      ]);

      // Log timing grouped by figure
      console.group(`%cETS Analytics Query Times (${fetchStartDate} to ${fetchEndDate})`, 'color: #af3eff; font-weight: bold');
      results.forEach(({ label, duration }) => {
        const color = duration > 3 ? 'color: #ef4444' : duration > 1 ? 'color: #f59e0b' : 'color: #10b981';
        console.log(`%c${duration}s %c- ${label}`, `${color}; font-weight: bold`, 'color: inherit');
      });
      const totalTime = results.reduce((sum, r) => Math.max(sum, Number(r.duration)), 0).toFixed(2);
      console.log(`%cTotal wall time: ~${totalTime}s (parallel)`, 'color: #6b7280; font-style: italic');
      console.groupEnd();

      const [statistics, countries, readingLevels, platforms] = results.map(r => r.result);

      setStats({
        activeUsers: statistics.activeUsers || 0,
        viewBooks: statistics.viewBooks || 0,
        viewDownloads: statistics.viewDownloads || 0,
        viewSeries: statistics.viewSeries || 0,
        booksRead: statistics.booksRead || 0,
        pagesRead: statistics.pagesRead || 0
      });
      if (statistics.earliestDate) setEarliestDate(statistics.earliestDate);

      setCountryData(countries || []);
      setReadingLevelsData(readingLevels || []);
      setPlatformData(platforms || []);

      setLoading(false);
      setRefreshing(false);
      setTimeout(() => setIsLoaded(true), 100);
    } catch (err) {
      console.error('Error loading ETS analytics data:', err);
      setError(err.message || 'Failed to load ETS analytics data');
      setLoading(false);
      setRefreshing(false);
    }
  }, [startDate, endDate]);

  // Initial load
  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    loadData(false, defaultRange.startDate, defaultRange.endDate);
    // eslint-disable-next-line
  }, []);

  // Handle filter apply
  const handleApplyFilters = (directStartDate = null, directEndDate = null) => {
    const fetchStartDate = directStartDate ?? startDate;
    const fetchEndDate = directEndDate ?? endDate;
    loadData(true, fetchStartDate, fetchEndDate);
  };

  // Get period label
  const getPeriodLabel = () => {
    if (!startDate || !endDate) return 'All Time';
    const days = getDaysBetween(startDate, endDate) + 1;
    if (days === 1) return 'Today';
    if (days === 7) return 'Last 7 Days';
    if (days === 30) return 'Last 30 Days';
    if (days === 90) return 'Last 90 Days';
    return `${days} Days`;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="error-state">
        <div className="error-message">
          <h2>Loading Your Data</h2>
          <p>Please refresh to try again.</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`home-page page-container ${isLoaded ? 'loaded' : ''}`}>
      {refreshing && <LoadingOverlay />}

      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>ETS Analytics</h1>
          <div className="page-header-badges">
            <div className="control-badge">
              <span className="badge-label">Period</span>
              <span className="badge-value">{getPeriodLabel()}</span>
            </div>
            <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleDarkMode} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="data-page-content">
        <div className="filters-container">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={handleApplyFilters}
            earliestDate={earliestDate}
            defaultDays={30}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-container">
        <div className="summary-card card-1">
          <div className="card-icon">
            <FaBookReader size={24} />
          </div>
          <div className="card-content">
            <h3>Books Read</h3>
            <p className="card-value" title={stats.booksRead.toLocaleString()}>{formatCompactNumber(stats.booksRead)}</p>
          </div>
        </div>

        <div className="summary-card card-2">
          <div className="card-icon">
            <FaFileAlt size={24} />
          </div>
          <div className="card-content">
            <h3>Pages Read</h3>
            <p className="card-value" title={stats.pagesRead.toLocaleString()}>{formatCompactNumber(stats.pagesRead)}</p>
          </div>
        </div>

        <div className="summary-card card-3">
          <div className="card-icon">
            <FaUsers size={24} />
          </div>
          <div className="card-content">
            <h3>Active Users</h3>
            <p className="card-value" title={stats.activeUsers.toLocaleString()}>{formatCompactNumber(stats.activeUsers)}</p>
          </div>
        </div>

        <div className="summary-card card-4">
          <div className="card-icon">
            <FaBook size={24} />
          </div>
          <div className="card-content">
            <h3>View Books</h3>
            <p className="card-value" title={stats.viewBooks.toLocaleString()}>{formatCompactNumber(stats.viewBooks)}</p>
          </div>
        </div>

        <div className="summary-card card-5">
          <div className="card-icon">
            <FaDownload size={24} />
          </div>
          <div className="card-content">
            <h3>View Downloads</h3>
            <p className="card-value" title={stats.viewDownloads.toLocaleString()}>{formatCompactNumber(stats.viewDownloads)}</p>
          </div>
        </div>

        <div className="summary-card card-6">
          <div className="card-icon">
            <FaLayerGroup size={24} />
          </div>
          <div className="card-content">
            <h3>View Series</h3>
            <p className="card-value" title={stats.viewSeries.toLocaleString()}>{formatCompactNumber(stats.viewSeries)}</p>
          </div>
        </div>
      </div>

      {/* Charts: Reading Levels + Platform */}
      <div className="charts-grid charts-grid-2col">
        <div className="chart-item" style={{ animationDelay: '0.3s' }}>
          <ReadingLevelsChart data={readingLevelsData} isDarkMode={isDarkMode} periodLabel={getPeriodLabel()} />
        </div>
        <div className="chart-item" style={{ animationDelay: '0.4s' }}>
          <PlatformChart data={platformData} isDarkMode={isDarkMode} periodLabel={getPeriodLabel()} />
        </div>
      </div>

      {/* Heatmap */}
      <div className="charts-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div className="chart-item" style={{ animationDelay: '0.5s' }}>
          <ActiveUsersHeatmap data={countryData} isDarkMode={isDarkMode} periodLabel={getPeriodLabel()} />
        </div>
      </div>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p>Copyright &copy; 2026 Booktime. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default EtsAnalytics;
